<script lang="ts" setup>
import { IonApp, IonRouterOutlet } from '@ionic/vue';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { onMounted, onUnmounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useFlowBackButton } from '@/composables/useFlowBackButton';
import { updateDevice } from '@/services/devices';
import { initProCache } from '@/services/auth';
import { useDeepLinks } from '@/composables/useDeepLinks';
import { useTutorial } from '@/composables/useTutorial';
import NetworkBanner from '@/atoms/NetworkBanner.vue';
import TutorialOverlay from '@/atoms/TutorialOverlay.vue';
import { LINKS } from '@/utils/links';
import { getApplicationMinimumVersion } from '@/services/settings';
import packageJson from '../package.json';
import semver from 'semver/preload';
import { useBottomSheet } from '@/composables/useBottomSheet';
import UpdateAppBottomSheet from '@/UpdateAppBottomSheet.vue';
import { Device } from '@capacitor/device';
import { PushNotifications } from '@capacitor/push-notifications';

useFlowBackButton();

const hideKeyboardOnScroll = () => Keyboard.hide();

const { initDeepLinks } = useDeepLinks();
const { checkAndStart } = useTutorial();
const route = useRoute();
const { open } = useBottomSheet();

watch(
  () => route.path,
  (path) => {
    if (path === LINKS.TABS.DASHBOARD) {
      checkAndStart();
    }
  },
  { immediate: true },
);

onMounted(async () => {
  await initProCache();
  await initDeepLinks();

  // La vérification de version est non critique : une erreur réseau (backend
  // indisponible, appel non authentifié, CORS…) ne doit jamais bloquer le
  // démarrage de l'app.
  try {
    const currentVersion = packageJson.version;
    const applicationMinimumVersion = await getApplicationMinimumVersion();

    if (semver.gt(applicationMinimumVersion, currentVersion)) {
      open(UpdateAppBottomSheet, null, null, false);
    }
  } catch (error) {
    console.warn('Impossible de récupérer la version minimale de l\'application', error);
  }

  if (Capacitor.getPlatform() === 'android') {
    const info = await Device.getInfo();
    const sdkVersion = info.androidSDKVersion ?? 0;

    if (sdkVersion >= 26) {
      await PushNotifications.createChannel({
        id: 'urgent_channel',
        name: 'Urgent Notifications',
        importance: 5,
        visibility: 1,
        sound: 'default',
        vibration: true,
        lights: true,
      });
    }
  }

  if (Capacitor.isNativePlatform()) {
    await StatusBar.setOverlaysWebView({ overlay: true });
    await StatusBar.setStyle({ style: Style.Light });
    if (Capacitor.getPlatform() === 'android') {
      await StatusBar.setBackgroundColor({ color: '#00000000' });
    }
    await updateDevice();

    // Ferme le clavier dès que l'utilisateur commence à scroller n'importe quel IonContent.
    // Utilise la capture pour attraper tous les ionScrollStart de l'app sans toucher chaque page.
    document.addEventListener('ionScrollStart', hideKeyboardOnScroll, true);
  }
});

onUnmounted(() => {
  document.removeEventListener('ionScrollStart', hideKeyboardOnScroll, true);
});
</script>

<template>
  <ion-app class="bg-white pt-safe pb-safe" data-cy-root>
    <NetworkBanner />
    <ion-router-outlet />
    <TutorialOverlay />
  </ion-app>
</template>
